/**
 * Un parent défie son enfant sur des SKILLCARDS : un défi live PRIVÉ où seul cet enfant est invité (et
 * prévenu). Deux entrées : la page d'une SKILLCARD (« Défier mon enfant », la carte est choisie) et la carte
 * de l'enfant sur l'accueil parent (« Défier Paul », on coche parmi SES SKILLCARDS).
 *
 * Le serveur (`live-private-create`) revérifie le lien parent ⇄ enfant, n'invite que lui, déduit niveau et
 * matière (niveau de l'enfant, matière de la première carte) et tire les questions parmi celles ouvertes à
 * l'ENFANT — ses cartes achetées ou offertes, sinon l'essai gratuit de chaque carte.
 */

import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { Check, Swords, X } from 'lucide-react';
import { MathText } from '@jeraisonne/ui';
import { stripMath } from '@jeraisonne/db/cards';
import { useAuth } from '@/lib/auth';
import { useStudentSkillcards } from '@/lib/cards/student-skillcards';
import { useChildrenAccess } from '@/lib/parent/subscriptions';
import { randomPresetPath, setPrivateLiveCover } from '@/lib/live-covers';
import {
  EXERCISE_CHALLENGE_SHEET_MS,
  PRIVATE_LIVE_MAX_QUESTIONS,
  privateLiveErrorMessage,
  useCreatePrivateLive,
  warmPrivateLivePreview,
  type CreatePrivateLiveInput,
} from '@/lib/private-live';

/** SKILLCARDS d'un défi : 20 au plus (limite du serveur). */
export const CHILD_CHALLENGE_MAX_CARDS = 20;

export interface ChallengeChild {
  id: string;
  name: string;
}

/** Prénom affichable (« Paul »). PUR. */
export function childFirstName(name: string | null | undefined): string {
  return (name ?? '').trim().split(/\s+/)[0] || 'ton enfant';
}

/** Titre du défi : « Défi : Pythagore » / « Défi : 3 SKILLCARDS » (80 car.). PUR. */
export function childChallengeTitle(cardTitles: string[]): string {
  const clean = cardTitles.map((t) => stripMath(t).replace(/\s+/g, ' ').trim()).filter(Boolean);
  if (clean.length === 1) return `Défi : ${clean[0]}`.slice(0, 80);
  return `Défi : ${clean.length} SKILLCARDS`;
}

/** Demande de création : les cartes choisies, l'enfant seul invité, départ quand le parent lance. PUR. */
export function childChallengeInput(
  childId: string,
  cards: { id: string; title: string }[],
): CreatePrivateLiveInput {
  const picked = cards.slice(0, CHILD_CHALLENGE_MAX_CARDS);
  return {
    // niveau et matière : déduits par le serveur (niveau de l'enfant, matière des cartes)
    level: '',
    subject: '',
    pack_id: null,
    card_ids: picked.map((c) => c.id),
    exercise_id: null,
    count: PRIVATE_LIVE_MAX_QUESTIONS,
    format: 'sheet',
    final_sheet: false,
    sheet_ms: EXERCISE_CHALLENGE_SHEET_MS,
    progressive: true,
    difficulty: 'medium',
    title: childChallengeTitle(picked.map((c) => c.title)),
    start: { mode: 'manual' },
    invitees: [childId],
  };
}

/** Les enfants liés du parent connecté (prénom, id). */
function useMyChildren(): ChallengeChild[] | null {
  const { user, profile } = useAuth();
  const isParent = profile?.role === 'parent';
  const { data } = useChildrenAccess(isParent ? (user?.id ?? '') : '');
  if (!isParent || !data) return null;
  return data.map((c) => ({ id: c.child_id, name: childFirstName(c.display_name) }));
}

const BTN =
  'border-ink-200 text-ink-700 font-display inline-flex min-h-11 w-full items-center justify-center gap-2 rounded-2xl border bg-white px-4 py-3 text-sm font-bold active:scale-[.98]';

/** Page d'une SKILLCARD : « Défier mon enfant » sur CETTE carte (parent seulement). */
export function ChallengeMyChildButton({ card }: { card: { id: string; title: string } }) {
  const children = useMyChildren();
  const [open, setOpen] = useState(false);
  if (!children || children.length === 0) return null;
  const label = children.length === 1 ? `Défier ${children[0]!.name}` : 'Défier mon enfant';
  return (
    <>
      <button
        type="button"
        className={BTN}
        data-testid="challenge-child"
        onClick={() => setOpen(true)}
      >
        <Swords className="h-4 w-4" aria-hidden />
        {label} sur cette SKILLCARD
      </button>
      {open && (
        <ChildChallengeDialog kids={children} fixedCards={[card]} onClose={() => setOpen(false)} />
      )}
    </>
  );
}

/** Accueil parent : « Défier Paul », on choisit parmi SES SKILLCARDS. */
export function ChallengeChildButton({ child }: { child: ChallengeChild }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button
        type="button"
        className={BTN}
        data-testid="challenge-child"
        onClick={() => setOpen(true)}
      >
        <Swords className="h-4 w-4" aria-hidden />
        Défier {child.name}
      </button>
      {open && <ChildChallengeDialog kids={[child]} onClose={() => setOpen(false)} />}
    </>
  );
}

function ChildChallengeDialog({
  kids,
  fixedCards,
  onClose,
}: {
  kids: ChallengeChild[];
  /** la carte de la page (sinon on coche parmi celles de l'enfant) */
  fixedCards?: { id: string; title: string }[];
  onClose: () => void;
}) {
  const [childId, setChildId] = useState(kids.length === 1 ? kids[0]!.id : '');
  const child = kids.find((k) => k.id === childId) ?? null;
  const cards = useStudentSkillcards(fixedCards ? null : childId || null);
  const [picked, setPicked] = useState<string[]>([]);
  const create = useCreatePrivateLive();
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = previous;
      window.removeEventListener('keydown', onKey);
    };
  }, [onClose]);

  const chosen = fixedCards ?? (cards.data ?? []).filter((c) => picked.includes(c.id));
  const toggle = (id: string) =>
    setPicked((p) =>
      p.includes(id)
        ? p.filter((x) => x !== id)
        : p.length >= CHILD_CHALLENGE_MAX_CARDS
          ? p
          : [...p, id],
    );

  const submit = () => {
    if (!child || chosen.length === 0) return;
    setError(null);
    create.mutate(childChallengeInput(child.id, chosen), {
      onSuccess: async (r) => {
        await setPrivateLiveCover(r.challenge_id, randomPresetPath()).catch(() => undefined);
        warmPrivateLivePreview(r.join_code);
        window.location.href = `/defi-live/${r.join_code}`;
      },
      onError: (e) => setError(privateLiveErrorMessage(e.message)),
    });
  };

  return createPortal(
    <div
      className="fixed inset-0 z-[80] flex items-end justify-center bg-black/60 sm:items-center sm:p-4"
      onClick={onClose}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="child-challenge-title"
        className="ocean:bg-[#061433] pb-safe flex max-h-[92dvh] w-full max-w-md flex-col gap-4 rounded-t-3xl bg-white p-5 shadow-2xl sm:rounded-3xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-3">
          <h2 id="child-challenge-title" className="font-display text-ink-900 text-lg font-bold">
            {child ? `Défier ${child.name}` : 'Défier mon enfant'}
          </h2>
          <button
            type="button"
            onClick={onClose}
            aria-label="Fermer"
            className="text-ink-500 -m-2 flex h-11 w-11 shrink-0 items-center justify-center rounded-full"
          >
            <X className="h-5 w-5" aria-hidden />
          </button>
        </div>

        {kids.length > 1 && (
          <div role="radiogroup" aria-label="Quel enfant ?" className="flex flex-wrap gap-2">
            {kids.map((k) => (
              <button
                key={k.id}
                type="button"
                role="radio"
                aria-checked={childId === k.id}
                onClick={() => setChildId(k.id)}
                className={`min-h-11 rounded-full border px-4 text-sm font-semibold ${
                  childId === k.id
                    ? 'border-primary-500 bg-primary-500 text-white'
                    : 'border-ink-200 text-ink-700'
                }`}
              >
                {k.name}
              </button>
            ))}
          </div>
        )}

        {fixedCards ? (
          <p className="text-ink-700 text-sm">
            Un défi de {PRIVATE_LIVE_MAX_QUESTIONS} questions sur «{' '}
            <MathText>{fixedCards[0]!.title}</MathText> », sur la feuille de brouillon.{' '}
            {child ? `${child.name} est prévenu·e` : 'Ton enfant est prévenu·e'} et vous jouez en
            même temps.
          </p>
        ) : (
          <div className="min-h-0 flex-1 space-y-2 overflow-y-auto">
            <p className="text-ink-600 text-sm">
              Choisis les SKILLCARDS du défi ({PRIVATE_LIVE_MAX_QUESTIONS} questions, sur la
              feuille).
            </p>
            {cards.isLoading && <div className="bg-ink-100 h-24 animate-pulse rounded-xl" />}
            {cards.data && cards.data.length === 0 && (
              <p className="text-ink-500 text-sm">
                {child?.name ?? 'Ton enfant'} n’a encore aucune SKILLCARD.
              </p>
            )}
            <ul className="space-y-1.5" aria-label="SKILLCARDS">
              {(cards.data ?? []).map((c) => {
                const on = picked.includes(c.id);
                return (
                  <li key={c.id}>
                    <button
                      type="button"
                      role="checkbox"
                      aria-checked={on}
                      onClick={() => toggle(c.id)}
                      className={`flex min-h-11 w-full items-center gap-3 rounded-xl border px-3 py-2 text-left text-sm ${
                        on ? 'border-primary-500 bg-primary-50' : 'border-ink-100'
                      }`}
                    >
                      <span
                        className={`flex h-5 w-5 shrink-0 items-center justify-center rounded border ${
                          on ? 'border-primary-500 bg-primary-500 text-white' : 'border-ink-300'
                        }`}
                        aria-hidden
                      >
                        {on && <Check className="h-3.5 w-3.5" />}
                      </span>
                      <span className="text-ink-900 min-w-0 flex-1 truncate font-semibold">
                        <MathText>{c.title}</MathText>
                      </span>
                      <span className="text-ink-500 shrink-0 text-xs">{c.mastery} %</span>
                    </button>
                  </li>
                );
              })}
            </ul>
          </div>
        )}

        {error && (
          <p role="alert" className="text-error-600 text-sm">
            {error}
          </p>
        )}

        <button
          type="button"
          onClick={submit}
          disabled={!child || chosen.length === 0 || create.isPending}
          data-testid="challenge-child-create"
          className="bg-accent-400 font-display text-ink-900 inline-flex min-h-11 w-full items-center justify-center gap-2 rounded-2xl px-6 py-3 text-base font-bold disabled:opacity-50"
        >
          <Swords className="h-4 w-4" aria-hidden />
          {create.isPending ? 'Préparation…' : 'Lancer le défi'}
        </button>
      </div>
    </div>,
    document.body,
  );
}
